/**
 * Button — geteiltes CTA-Primitive (Sprint UI-D1, erweitert Post-UI-D5 mit
 * asChild + variant=link).
 *
 * 6 Variants (+ link), 2 Sizes, loading-State via Spinner, durchgängige focus-visible-
 * Ringe, Token-Farben (kein hardcoded Hex). `forwardRef` ist Pflicht — D2's
 * Modal-Primitive nutzt Button als Footer/Trigger (patterns/react.md §
 * forwardRef-required). Ref-Type ist `HTMLElement` weil asChild auf
 * Nicht-Button-Elemente rendern kann (`<a>`, wouter `<Link>`).
 *
 * `className` wird als LETZTES an den class-string angehängt — gedacht für
 * ADDITIVE Caller-Utilities (Breite, Margin, Positionierung), NICHT zum
 * Überschreiben von Variant-Farben/Padding. Kein `tailwind-merge`.
 *
 * **asChild?: boolean** (Post-UI-D5): rendert NICHT `<button>`, sondern
 * merged Klassen/Refs/Props auf das einzige Kind-Element via cloneElement
 * (Slot-Pattern). Eigene Impl statt @radix-ui/react-slot (Projekt ist
 * Radix-frei). Bei asChild=true:
 * - leadingIcon/trailingIcon/loading-Spinner werden IGNORIERT (Caller liefert
 *   eigenes Markup)
 * - `disabled` und `type`-Attrs werden SUPPRESSED auf Kind (invalid auf <a>);
 *   stattdessen aria-disabled + tabIndex=-1 + data-disabled + click-prevent
 * - Event-Handler-Composition: child-onClick FIRST, dann Button-onClick (nur
 *   wenn !event.defaultPrevented)
 * - className-Merge-Order: Button-base/variant/size → Button.className → child.className
 *
 * **variant="link"** (Post-UI-D5): text-link styling (kein bg, kein padding).
 * `size`-Prop wird für `variant="link"` IGNORIERT.
 */
import {
  Children,
  cloneElement,
  Fragment,
  forwardRef,
  isValidElement,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { Spinner } from './Spinner';

export type ButtonVariant =
  | 'primary'
  | 'cta'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'danger-outline'
  | 'link';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Zeigt einen Spinner, erzwingt `disabled` + `aria-busy`. */
  loading?: boolean;
  fullWidth?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  /** Rendert das einzige Kind-Element statt `<button>` (Slot-Pattern). */
  asChild?: boolean;
  children: ReactNode;
}

// Redesign S51: transition (statt nur -colors) → transform+shadow animieren mit;
// active:scale = taktiler Press. Hover-Lift/Shadow pro Variant (unten).
const BASE =
  'inline-flex items-center justify-center gap-2 rounded-control font-medium transition duration-150 ease-out active:scale-[0.98] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500 focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 disabled:hover:translate-y-0 disabled:hover:shadow-none';

/** Eigener BASE für variant=link — KEIN bg/padding (text-link). */
const BASE_LINK =
  'inline-flex items-center gap-1 rounded font-medium transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500 focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const VARIANT: Record<ButtonVariant, string> = {
  // Redesign S2 (2026-10-10): Hover bleibt grün (dunkler) — Gelb gehört genau
  // EINER Aktion pro Screen (Hochladen, Variante `cta`).
  primary:
    'bg-brand-green-700 text-white hover:bg-brand-green-800 hover:-translate-y-px hover:shadow-soft',
  // Gelber CTA — app-weit nur „Hochladen" (Regel „Gelb genau einmal pro Screen",
  // tasks/design-handoff-redesign-s2.md). ink-900 auf #FFC836 = AA.
  cta: 'bg-brand-yellow text-ink-900 font-bold hover:brightness-95 hover:-translate-y-px hover:shadow-soft',
  secondary:
    'bg-white border border-ink-200 text-brand-green-800 hover:bg-ink-50 hover:border-ink-300 hover:-translate-y-px hover:shadow-soft',
  ghost: 'bg-transparent text-brand-green-800 hover:bg-ink-100',
  danger: 'bg-red-600 text-white hover:bg-red-700 hover:-translate-y-px hover:shadow-soft',
  // Outline-Danger: destruktive Aktion, die klar als Button lesbar bleibt, aber
  // nicht die solide-rote Dominanz hat (für In-Table-Aktionen). Solid-`danger`
  // bleibt dem Confirm-Dialog vorbehalten.
  'danger-outline': 'bg-white border border-red-200 text-red-700 hover:bg-red-50 hover:border-red-300',
  link: 'bg-transparent text-brand-green-700 hover:underline',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
};

// ── asChild Slot-Helpers ──────────────────────────────────────────────

type ChildElementProps = {
  className?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  tabIndex?: number;
};

/** Kombiniert mehrere Refs auf dasselbe Element. */
function mergeRefs<T>(
  ...refs: Array<Ref<T> | undefined>
): Ref<T> {
  return (value: T | null) => {
    refs.forEach((r) => {
      if (typeof r === 'function') r(value as T);
      else if (r != null) (r as { current: T | null }).current = value;
    });
  };
}

// ── Component ─────────────────────────────────────────────────────────

export const Button = forwardRef<HTMLElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    leadingIcon,
    trailingIcon,
    children,
    className,
    type,
    disabled,
    asChild = false,
    onClick,
    ...rest
  },
  ref,
) {
  // variant=link nutzt BASE_LINK + IGNORIERT size
  const isLink = variant === 'link';
  const baseSet = isLink ? BASE_LINK : BASE;
  const sizeSet = isLink ? '' : SIZE[size];
  const cls = [
    baseSet,
    VARIANT[variant],
    sizeSet,
    fullWidth ? 'w-full' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  if (asChild) {
    const child = Children.only(children);
    if (!isValidElement(child)) {
      throw new Error('<Button asChild> requires a single valid React element child.');
    }
    // Fragment-Reject (Codex spec-eval R1 P2-#1)
    if ((child as ReactElement).type === Fragment) {
      throw new Error('<Button asChild> does not support React fragments as children.');
    }
    const childProps = (child as ReactElement<ChildElementProps>).props ?? {};
    const childRef = (child as ReactElement & { ref?: Ref<HTMLElement> }).ref;

    const isDisabled = disabled === true || loading === true;

    // Event-Handler-Composition (Codex spec-eval R1 P1-#2): child first,
    // Button-onClick wenn !defaultPrevented. Bei disabled: beide suppressed.
    const composedOnClick = isDisabled
      ? (e: MouseEvent<HTMLElement>) => {
          e.preventDefault();
          e.stopPropagation();
        }
      : (e: MouseEvent<HTMLElement>) => {
          childProps.onClick?.(e);
          if (!e.defaultPrevented && onClick) {
            (onClick as (e: MouseEvent<HTMLElement>) => void)(e);
          }
        };

    // className merge-order (Codex spec-eval R1 P2-#3): Button-base → child-className (last)
    const mergedClassName = [cls, childProps.className ?? ''].filter(Boolean).join(' ');

    return cloneElement(
      child,
      {
        ...rest,
        className: mergedClassName,
        onClick: composedOnClick,
        'aria-busy': loading ? true : undefined,
        'aria-disabled': isDisabled ? true : undefined,
        tabIndex:
          isDisabled && childProps.tabIndex === undefined ? -1 : childProps.tabIndex,
        'data-disabled': isDisabled ? '' : undefined,
        ref: mergeRefs<HTMLElement>(ref, childRef),
      } as Record<string, unknown>,
    );
  }

  return (
    <button
      ref={ref as Ref<HTMLButtonElement>}
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading ? true : undefined}
      onClick={onClick}
      className={cls}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
});

Button.displayName = 'Button';
